import EmailComposerSubjectRow from '@/components/email-composer/EmailComposerSubjectRow';
import type { AvailableVariable } from '@fluenceur/ui';
import { useCallback, useState, type ReactNode } from 'react';

interface OutreachSubjectState {
  /** Editable subject line with the variable dropdown; render above the composer. */
  subjectRow: ReactNode;
  /** Live subject: the seed until the brand edits it. Chips stay `{{key}}` text. */
  subject: string;
  /** False on an empty (or whitespace-only) subject: never sendable. */
  isSubjectValid: boolean;
}

/**
 * Editable-subject state seeded from a default that changes with the body
 * language. A new default re-seeds the subject and bumps the editor remount key
 * so it re-hydrates — a manual edit is dropped on a language switch, which
 * mirrors what the body already does (an untouched body re-seeds from the new
 * language's template).
 *
 * The seed is adjusted during render rather than in an effect so the reported
 * subject is never one render stale — a send fired in that window would ship the
 * previous language's subject.
 *
 * Owns the row's rendering too, so its wiring cannot drift between surfaces.
 */
export const useOutreachSubjectState = (
  defaultSubject: string,
  availableVariables: AvailableVariable[]
): OutreachSubjectState => {
  const [state, setState] = useState({
    seed: defaultSubject,
    subject: defaultSubject,
    resetKey: 0,
  });

  if (state.seed !== defaultSubject) {
    setState({ seed: defaultSubject, subject: defaultSubject, resetKey: state.resetKey + 1 });
  }

  const setSubject = useCallback((plainText: string) => {
    setState((previous) => ({ ...previous, subject: plainText }));
  }, []);
  const { subject } = state;

  const subjectRow = (
    <EmailComposerSubjectRow
      resetKey={state.resetKey}
      initialContent={state.seed}
      availableVariables={availableVariables}
      onContentChange={setSubject}
      // Standalone subject row (no sibling De/Destinataires/Programme rows to
      // align with), so the label hugs its text rather than reserving the
      // shared fixed width and leaving a visible gap.
      labelWidth="auto"
    />
  );

  return {
    subjectRow,
    subject,
    isSubjectValid: subject.trim().length > 0,
  };
};
